import merge from 'lodash-es/merge';
export class FetchClientError extends Error {
    abortType;
    status;
    responseBody;
    constructor(message, abortType, status, responseBody) {
        super(message);
        this.abortType = abortType;
        this.status = status;
        this.responseBody = responseBody;
    }
}
export class Problem extends FetchClientError {
    constructor(responseBody) {
        super(`[${responseBody.status} - ${responseBody.type}] ${responseBody.title}`, AbortType.UNKNOWN, responseBody.status, responseBody);
    }
    get detail() {
        return this.responseBody.detail;
    }
    get instance() {
        return this.responseBody.instance;
    }
    get title() {
        return this.responseBody.title;
    }
    get traceId() {
        return this.responseBody.traceId;
    }
    get type() {
        return this.responseBody.type;
    }
    static toJson(error) {
        return {
            detail: error.detail,
            instance: error.instance,
            status: error.status,
            title: error.title,
            traceId: error.traceId,
            type: error.type,
        };
    }
    static isProblem(error) {
        if (error !== null && error !== undefined && typeof error === 'object') {
            return 'status' in error && 'title' in error && 'traceId' in error && 'type' in error;
        }
        return false;
    }
}
export var AbortType;
(function (AbortType) {
    AbortType[AbortType["EXPLICIT"] = 0] = "EXPLICIT";
    AbortType[AbortType["UNMOUNT"] = 1] = "UNMOUNT";
    AbortType[AbortType["UNKNOWN"] = 2] = "UNKNOWN";
})(AbortType || (AbortType = {}));
export default class FetchClient {
    _baseUrl = null;
    _optionsCallback = () => Promise.resolve({
        credentials: 'same-origin',
    });
    constructor(options) {
        if (options?.optionsCallback) {
            this._optionsCallback = options?.optionsCallback;
        }
        if (options?.baseUrl) {
            this._baseUrl = options.baseUrl.replace(/\/+$/, ''); // Trim trailing slashes
        }
    }
    async getFetchOptions(...overrideOptions) {
        const defaultOptions = await this._optionsCallback();
        const options = {};
        merge(options, defaultOptions, ...overrideOptions);
        return options;
    }
    async handleErrorResponse(response) {
        if (response.bodyUsed) {
            return new FetchClientError(`Request failed (Status ${response.status}): ${response.statusText}`, AbortType.UNKNOWN, response.status, null);
        }
        else {
            const contentType = response.headers.get('Content-Type');
            let responseData = null;
            if (contentType && contentType.match(/application\/.*json/)) {
                responseData = (await response.json());
                if (Problem.isProblem(responseData)) {
                    return new Problem(responseData);
                }
            }
            else if (contentType && contentType.match(/text\/.+/)) {
                responseData = await response.text();
            }
            else {
                responseData = await response.arrayBuffer();
            }
            return new FetchClientError(`Request failed (Status ${response.status}): ${response.statusText}`, AbortType.UNKNOWN, response.status, responseData);
        }
    }
    executeRequest(url, options) {
        const abortController = new AbortController();
        if (this._baseUrl && !(url.startsWith('/') || url.startsWith('http'))) {
            url = `${this._baseUrl}/${url}`;
        }
        return {
            abort: () => abortController.abort('Aborted'),
            response: (async () => {
                const mergedOptions = await this.getFetchOptions({ signal: abortController.signal }, options);
                const response = await fetch(url, mergedOptions);
                if (response.ok) {
                    return response;
                }
                else {
                    const err = await this.handleErrorResponse(response);
                    throw err;
                }
            })(),
        };
    }
    get(url, overrideOptions = {}) {
        return this.executeRequest(url, merge({
            method: 'GET',
        }, overrideOptions));
    }
    post(url, body, overrideOptions = {}) {
        return this.executeRequest(url, merge({
            method: 'POST',
            body,
        }, overrideOptions));
    }
    put(url, body, overrideOptions = {}) {
        return this.executeRequest(url, merge({
            method: 'PUT',
            body,
        }, overrideOptions));
    }
    delete(url, overrideOptions = {}) {
        return this.executeRequest(url, merge({
            method: 'DELETE',
        }, overrideOptions));
    }
    getJson(url, overrideOptions = {}) {
        const abortableRequest = this.get(url, merge({
            headers: {
                Accept: 'application/json',
            },
        }, overrideOptions));
        return {
            abort: abortableRequest.abort,
            response: abortableRequest.response.then((response) => response.json()),
        };
    }
    postJson(url, body, overrideOptions = {}) {
        const abortableRequest = this.post(url, JSON.stringify(body), merge({
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
        }, overrideOptions));
        return {
            abort: abortableRequest.abort,
            response: abortableRequest.response.then((response) => response.json()),
        };
    }
    putJson(url, body, overrideOptions = {}) {
        const abortableRequest = this.put(url, JSON.stringify(body), merge({
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
        }, overrideOptions));
        return {
            abort: abortableRequest.abort,
            response: abortableRequest.response.then((response) => response.json()),
        };
    }
    deleteJson(url, overrideOptions = {}) {
        const abortableRequest = this.delete(url, merge({
            headers: {
                Accept: 'application/json',
            },
        }, overrideOptions));
        return {
            abort: abortableRequest.abort,
            response: abortableRequest.response.then((response) => response.json()),
        };
    }
    static autoAbort(request, registerAbort) {
        let rejectPromise;
        const abort = (type = AbortType.EXPLICIT) => {
            // This will implicitly cancel the query if it's still running
            request.abort();
            rejectPromise(type);
        };
        const unregisterAbort = registerAbort?.(abort);
        const dataPromise = new Promise((resolve, reject) => {
            request.response
                .then((response) => {
                unregisterAbort?.();
                resolve(response);
            })
                .catch(reject);
            rejectPromise = (type) => reject(new FetchClientError('Aborted', type, 0, null));
        });
        return {
            response: dataPromise,
            abort,
        };
    }
}
//# sourceMappingURL=FetchClient.js.map